"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.addInteractionData = exports.matchNodesWithPatterns = exports.getNodeData = exports.convertUserDefinedNameFilters = exports.gatherNodesForPattern = void 0;
const viewer_1 = require("@shapediver/viewer");
const InteractionData_1 = require("../InteractionData");
// #endregion Type aliases (2)
// #region Variables (7)
/**
 * The black list of node names that should be ignored.
 *
 * These node names will be ignored when checking the node names.
 * These names are used in the ShapeDiver GH plugin for certain operations.
 */
const NODE_NAME_BLACKLIST = ['TransformZUpToYUp', 'no_transformations'];
/**
 * Recurse the scene tree downwards starting from the given node, gather all nodes that match the pattern,
 * and add them to the result array.
 *
 * @param node The node to start traversing from. Typically this is the node of an output of a ShapeDiver model.
 * @param pattern The hierarchical pattern to check for.
 *                Each string of the pattern represents a regular expression for matching the node name.
 * @param outputApiName The name of the output API to be used for the concatenated node name.
 * @param result The result object, matching nodes will be added here.
 * @param count The current index into the pattern array.
 */
const gatherNodesForPattern = (node, pattern, outputApiName, result, count = 0) => {
    var _a;
    // if the node has no original name (was not given a name in Grasshopper) or 
    // its name matches the black list, do not consider it for pattern matching
    if (!node.originalName || NODE_NAME_BLACKLIST.includes(node.originalName)) {
        for (const child of node.children) {
            (0, exports.gatherNodesForPattern)(child, pattern, outputApiName, result, count);
        }
    }
    // if the original name matches the pattern, check the children
    else if (node.originalName && new RegExp(`^${pattern[count]}$`).test(node.originalName)) {
        if (count === pattern.length - 1) {
            // we reached the end of the pattern, add the node to the result
            result[node.id] = {
                node,
                name: outputApiName + '.' + ((_a = (0, exports.getNodeData)(node)) === null || _a === void 0 ? void 0 : _a.originalName) || ''
            };
        }
        else {
            for (const child of node.children) {
                (0, exports.gatherNodesForPattern)(child, pattern, outputApiName, result, count + 1);
            }
        }
    }
};
exports.gatherNodesForPattern = gatherNodesForPattern;
/**
 * Convert the user-defined name-filters to filter patterns as used by useNodeInteractionData.
 *
 * The name filter is an array of dot-separated strings.
 * Each string represents a pattern to hierarchically match node names.
 * The first part of the pattern is the output name.
 * The rest of the pattern correspond to hierarchical node names, which may contain the "*"
 * character as a wildcard to match any node name or any part of the node name.
 *
 * @param nameFilter The user-defined name filters to convert.
 * @param outputIdsToNamesMapping A mapping of output IDs to output names for the session to be used.
 *
 * @returns The filter pattern object to be used with useNodeInteractionData, useSelection, and other interaction hooks.
 */
const convertUserDefinedNameFilters = (nameFilter, outputIdsToNamesMapping) => {
    const patterns = {};
    // we iterate over the name filter array
    // we store the result with the output ID as the key and an array of patterns as the value
    for (let i = 0; i < nameFilter.length; i++) {
        const parts = nameFilter[i].split('.');
        const outputName = parts[0];
        // replace the "*" with ".*" to create a regex pattern
        const outputNameRegex = new RegExp(`^${outputName.replace(/\*/g, '.*')}$`);
        // find the IDs of outputs whose names match
        const outputIds = Object.entries(outputIdsToNamesMapping).filter(([, name]) => outputNameRegex.test(name)).map(([id]) => id);
        // create a regex pattern from the other parts of the array
        // replace all "*" with ".*"
        const patternArray = parts.slice(1).map(part => part.replace(/\*/g, '.*'));
        // we iterate over the output mappings
        for (const outputId of outputIds) {
            // store the pattern in the pattern object
            if (!patterns[outputId])
                patterns[outputId] = [];
            patterns[outputId].push(patternArray);
        }
    }
    return patterns;
};
exports.convertUserDefinedNameFilters = convertUserDefinedNameFilters;
/**
 * Traverse the node hierarchy upwards to find the node that corresponds to an output
 * of the ShapeDiver model.
 * Return the node itself, the corresponding output id and name, and the original names
 * concatenated using dots.
 *
 * @param node The node to start the upwards traversal from.
 * @returns
 */
const getNodeData = (node) => {
    const names = [];
    let tempNode = node;
    while (tempNode && tempNode.parent) {
        if (tempNode.originalName && !NODE_NAME_BLACKLIST.includes(tempNode.originalName))
            names.push(tempNode.originalName);
        // look for the output API data in the node
        const data = tempNode.data.find((data) => data instanceof viewer_1.OutputApiData);
        if (data) {
            const api = data.api;
            return {
                outputId: api.id,
                outputName: api.name,
                originalName: names.reverse().join('.')
            };
        }
        tempNode = tempNode.parent;
    }
};
exports.getNodeData = getNodeData;
/**
 * Try to match the given node with the patterns.
 * In case of a match, return the concatenated name of the node as required
 * for setting values of interaction parameters.
 *
 * @param patterns
 * @param node
 * @returns
 */
const matchNodeWithPatterns = (patterns, node) => {
    var _a;
    const nodeData = (0, exports.getNodeData)(node);
    if (!nodeData)
        return;
    const { outputId, outputName, originalName } = nodeData;
    // check if the path matches the pattern and return the first match
    for (const pattern of (_a = patterns[outputId]) !== null && _a !== void 0 ? _a : []) {
        if (pattern.length === 0) {
            // special case, just the output name was provided
            return outputName;
        }
        else {
            // create a regex pattern from the pattern array, match the original name
            const match = originalName.match(`^${pattern.join('\\.')}$`);
            if (match)
                return outputName + '.' + match[0];
        }
    }
};
/**
 * Try to match the given nodes with the patterns.
 * For matching nodes, return the concatenated names of the nodes as required
 * for setting values of interaction parameters.
 *
 * @param patterns The patterns to match the node names.
 * @param nodes The nodes to process.
 * @returns The concatenated names of the nodes that match the pattern.
 */
const matchNodesWithPatterns = (patterns, nodes) => {
    // we iterate over the nodes and get the output and node names
    const nodeNames = [];
    nodes.forEach((node) => {
        const result = matchNodeWithPatterns(patterns, node);
        if (result)
            nodeNames.push(result);
    });
    return nodeNames;
};
exports.matchNodesWithPatterns = matchNodesWithPatterns;
/**
 * Add interaction data to the node.
 *
 * If the node already has interaction data, the function will remove the interaction data and add the new interaction data.
 * Then the function will update the version of the node.
 *
 * @param node
 * @param interactionDataSettings
 */
const addInteractionData = (node, interactionDataSettings, componentId) => {
    for (const data of node.data) {
        // remove existing interaction data if it is restricted to the current component
        if (data instanceof InteractionData_1.InteractionData && data.restrictedManagers.includes(componentId)) {
            console.warn(`Node ${node.id} already has interaction data with id ${data.id}, removing it.`);
            node.removeData(data);
        }
    }
    // add the interaction data to the node
    const interactionData = new InteractionData_1.InteractionData(interactionDataSettings, undefined, [componentId]);
    node.addData(interactionData);
    node.updateVersion();
};
exports.addInteractionData = addInteractionData;
// #endregion Variables (7)
//# sourceMappingURL=PatternUtils.js.map